import { useState, useEffect } from 'react';
import { workflowApi } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SearchInput } from '../components/ui/SearchInput';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  Workflow,
  MoreHorizontal,
  Play,
  Copy,
  Ban,
  CheckCircle,
  ChevronLeft,
  GitBranch,
  Clock,
  User,
  Eye,
  Download,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { WorkflowReadOnlyCanvas } from '../components/workflow/WorkflowReadOnlyCanvas';

interface WorkflowItem {
  id: string;
  name: string;
  description?: string;
  userId: string;
  username: string;
  nodeCount: number;
  runCount: number;
  lastRunAt?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface WorkflowDetail {
  id: string;
  name: string;
  description?: string;
  userId: string;
  username: string;
  nodeCount: number;
  runCount: number;
  lastRunAt?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  definition: {
    nodes: Array<{
      id: string;
      type: string;
      label: string;
      position: { x: number; y: number };
      config?: Record<string, unknown>;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
    }>;
  };
  stats: {
    totalRuns: number;
    successRate: number;
    avgExecutionTime: number;
  };
  runs: Array<{
    id: string;
    status: string;
    startedAt: string;
    endedAt?: string;
    duration: number;
  }>;
}

export function WorkflowsPage() {
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'structure' | 'runs' | 'stats'>('structure');

  const fetchWorkflows = async () => {
    setIsLoading(true);
    try {
      const response = await workflowApi.listWorkflows({
        page,
        pageSize,
        keyword: keyword || undefined,
        status: status || undefined,
      });
      if (response.code === 0) {
        setWorkflows(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, [page, pageSize, keyword, status]);

  const fetchWorkflowDetail = async (workflowId: string) => {
    try {
      const response = await workflowApi.getWorkflowDetail(workflowId);
      if (response.code === 0) {
        setSelectedWorkflow(response.data);
        setShowDetail(true);
      }
    } catch (err) {
      console.error('Failed to fetch workflow detail:', err);
    }
  };

  const handleDisableWorkflow = async (workflowId: string) => {
    try {
      await workflowApi.disableWorkflow(workflowId);
      fetchWorkflows();
    } catch (err) {
      console.error('Failed to disable workflow:', err);
    }
  };

  const handleEnableWorkflow = async (workflowId: string) => {
    try {
      await workflowApi.enableWorkflow(workflowId);
      fetchWorkflows();
    } catch (err) {
      console.error('Failed to enable workflow:', err);
    }
  };

  const handleCloneWorkflow = async (workflowId: string) => {
    try {
      await workflowApi.cloneWorkflow(workflowId);
      fetchWorkflows();
    } catch (err) {
      console.error('Failed to clone workflow:', err);
    }
  };

  const handleDebugRun = async (workflowId: string) => {
    try {
      const response = await workflowApi.debugRun(workflowId);
      if (response.code === 0) {
        alert(`调试运行成功！运行ID: ${response.data.runId}`);
      }
    } catch (err) {
      console.error('Failed to debug run:', err);
    }
  };

  const handleExportWorkflow = async (workflow: WorkflowItem) => {
    try {
      await workflowApi.exportWorkflow(workflow.id, workflow.name);
    } catch (err) {
      console.error('Failed to export workflow:', err);
      alert('导出工作流失败');
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('zh-CN');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">正常</Badge>;
      case 'inactive':
        return <Badge variant="warning">已禁用</Badge>;
      case 'error':
        return <Badge variant="error">错误</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (showDetail && selectedWorkflow) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => setShowDetail(false)}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900"
        >
          <ChevronLeft className="w-4 h-4" />
          返回工作流列表
        </button>

        {/* Workflow Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">{selectedWorkflow.name}</h1>
            <p className="text-zinc-500 mt-1">{selectedWorkflow.description || '无描述'}</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedWorkflow.status === 'active' ? (
              <Button variant="secondary" onClick={() => handleDisableWorkflow(selectedWorkflow.id)}>
                <Ban className="w-4 h-4" />
                禁用
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => handleEnableWorkflow(selectedWorkflow.id)}>
                <CheckCircle className="w-4 h-4" />
                启用
              </Button>
            )}
            <Button variant="secondary" onClick={() => handleCloneWorkflow(selectedWorkflow.id)}>
              <Copy className="w-4 h-4" />
              复制
            </Button>
            <Button variant="secondary" onClick={() => handleExportWorkflow(selectedWorkflow)}>
              <Download className="w-4 h-4" />
              导出
            </Button>
            <Button onClick={() => handleDebugRun(selectedWorkflow.id)}>
              <Play className="w-4 h-4" />
              调试运行
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <GitBranch className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">节点数量</p>
                  <p className="text-xl font-bold text-zinc-900">{selectedWorkflow.nodeCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Play className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">总运行次数</p>
                  <p className="text-xl font-bold text-zinc-900">{selectedWorkflow.stats.totalRuns}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-success-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-success-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">成功率</p>
                  <p className="text-xl font-bold text-zinc-900">{selectedWorkflow.stats.successRate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">平均执行时间</p>
                  <p className="text-xl font-bold text-zinc-900">{selectedWorkflow.stats.avgExecutionTime.toFixed(0)}ms</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-200">
          <nav className="flex gap-6">
            {[
              { key: 'structure', label: '工作流结构', icon: GitBranch },
              { key: 'runs', label: '运行记录', icon: Play },
              { key: 'stats', label: '统计数据', icon: Workflow },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={cn(
                  'flex items-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-accent-600 text-accent-600'
                    : 'border-transparent text-zinc-500 hover:text-zinc-700'
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'structure' && (
          <Card>
            <CardHeader>
              <CardTitle>工作流图示</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[500px] border border-zinc-200 rounded-xl overflow-hidden">
                <WorkflowReadOnlyCanvas
                  nodes={selectedWorkflow.definition.nodes.map(n => ({
                    id: n.id,
                    type: n.type,
                    label: n.label,
                    position: n.position,
                    config: n.config,
                  }))}
                  edges={selectedWorkflow.definition.edges.map(e => ({
                    from: e.source,
                    to: e.target,
                  }))}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'runs' && (
          <Card>
            <CardHeader>
              <CardTitle>运行记录</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>运行 ID</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>开始时间</TableHead>
                    <TableHead>结束时间</TableHead>
                    <TableHead>耗时</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedWorkflow.runs.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell className="font-mono text-sm">{run.id.slice(0, 8)}...</TableCell>
                      <TableCell>
                        <Badge variant={run.status === 'success' ? 'success' : run.status === 'failed' ? 'error' : 'warning'}>
                          {run.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(run.startedAt)}</TableCell>
                      <TableCell>{formatDate(run.endedAt)}</TableCell>
                      <TableCell>{run.duration}ms</TableCell>
                    </TableRow>
                  ))}
                  {selectedWorkflow.runs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                        暂无运行记录
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>执行统计</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-zinc-50 rounded-lg">
                  <span className="text-zinc-600">总运行次数</span>
                  <span className="text-xl font-bold text-zinc-900">{selectedWorkflow.stats.totalRuns}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-success-50 rounded-lg">
                  <span className="text-success-600">成功率</span>
                  <span className="text-xl font-bold text-success-600">{selectedWorkflow.stats.successRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-error-50 rounded-lg">
                  <span className="text-error-600">失败率</span>
                  <span className="text-xl font-bold text-error-600">{(100 - selectedWorkflow.stats.successRate).toFixed(1)}%</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>性能统计</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-zinc-50 rounded-lg">
                  <span className="text-zinc-600">平均执行时间</span>
                  <span className="text-xl font-bold text-zinc-900">{selectedWorkflow.stats.avgExecutionTime.toFixed(0)}ms</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-zinc-50 rounded-lg">
                  <span className="text-zinc-600">节点数量</span>
                  <span className="text-xl font-bold text-zinc-900">{selectedWorkflow.nodeCount}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-zinc-50 rounded-lg">
                  <span className="text-zinc-600">最后运行</span>
                  <span className="text-zinc-900">{formatDate(selectedWorkflow.lastRunAt)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">工作流管理</h1>
          <p className="text-zinc-500 mt-1">管理平台所有工作流</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              placeholder="搜索工作流名称..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onClear={() => setKeyword('')}
              className="sm:w-80"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            >
              <option value="">所有状态</option>
              <option value="active">正常</option>
              <option value="inactive">已禁用</option>
              <option value="error">错误</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Workflows Table */}
      <Card>
        <CardHeader>
          <CardTitle>工作流列表</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>工作流</TableHead>
                    <TableHead>所有者</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>节点数</TableHead>
                    <TableHead>运行次数</TableHead>
                    <TableHead>最后运行</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflows.map((workflow) => (
                    <TableRow key={workflow.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                            <Workflow className="w-5 h-5 text-accent-600" />
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900">{workflow.name}</p>
                            <p className="text-sm text-zinc-500 truncate max-w-[200px]">{workflow.description || '无描述'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span>{workflow.username}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(workflow.status)}</TableCell>
                      <TableCell>{workflow.nodeCount}</TableCell>
                      <TableCell>{workflow.runCount}</TableCell>
                      <TableCell>{formatDate(workflow.lastRunAt)}</TableCell>
                      <TableCell>{formatDate(workflow.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={() => setShowActionMenu(showActionMenu === workflow.id ? null : workflow.id)}
                            className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          
                          {showActionMenu === workflow.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg border border-zinc-200 shadow-lg z-50">
                              <button
                                onClick={() => {
                                  fetchWorkflowDetail(workflow.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Eye className="w-4 h-4" />
                                查看详情
                              </button>
                              {workflow.status === 'active' ? (
                                <button
                                  onClick={() => {
                                    handleDisableWorkflow(workflow.id);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-warning-600 hover:bg-warning-50"
                                >
                                  <Ban className="w-4 h-4" />
                                  禁用
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    handleEnableWorkflow(workflow.id);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-success-600 hover:bg-success-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  启用
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  handleCloneWorkflow(workflow.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Copy className="w-4 h-4" />
                                复制
                              </button>
                              <button
                                onClick={() => {
                                  handleExportWorkflow(workflow);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Download className="w-4 h-4" />
                                导出
                              </button>
                              <button
                                onClick={() => {
                                  handleDebugRun(workflow.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-accent-600 hover:bg-accent-50"
                              >
                                <Play className="w-4 h-4" />
                                调试运行
                              </button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
